<template>
  <div class="def-select-wrapper" :class="{ 'def-select-wrapper_disabled': disabled }">
    <input
      ref="hiddenInput"
      type="text"
      :name="name"
      :value="value"
      :required="required"
      class="def-select-wrapper__input"
      @invalid.prevent="isInvalid = true"
    />
    <div
      class="def-select-wrapper__front"
      :class="{
        'def-select-wrapper__front_active': isDefaultSlot,
        'def-select-wrapper__front_error': isInvalid,
      }"
      :style="$slots.placeholder ? `min-height: 54px` : null"
      @click="handlerToggle"
    >
      <span
        v-if="$slots.placeholder"
        class="def-select-wrapper__placeholder"
        :class="{ 'def-select-wrapper__center': !isHeader }"
      >
        <slot name="placeholder" />
      </span>
      <span v-if="isHeader" class="def-select-wrapper__header">
        <slot name="header" />
      </span>
      <svg
        v-if="isDefaultSlot"
        :class="{ 'def-select-wrapper__close_active': active }"
        class="def-select-wrapper__close"
      >
        <use xlink:href="#svg-select"></use>
      </svg>
    </div>
    <SlideUpDown v-model="active" class="def-select-wrapper__drop">
      <DefScrollbar class="def-select-wrapper__scroll">
        <span v-if="$slots.search" class="def-select-wrapper__search">
          <slot name="search" />
          <svg class="def-select-wrapper__magnifying"><use xlink:href="#svg-magnifying"></use></svg>
        </span>
        <div class="def-select-wrapper__items" @click="handlerClick">
          <slot />
        </div>
      </DefScrollbar>
    </SlideUpDown>
  </div>
</template>

<script>
import SlideUpDown from 'vue3-slide-up-down';
import useScreenWidth from '@use/useScreenWidth';
import DefScrollbar from '@components/Def/DefScrollbar/DefScrollbar.vue';

export default {
  name: 'DefSelectWrapper',
  components: {
    DefScrollbar,
    SlideUpDown,
  },
  props: {
    name: {
      type: String,
    },
    value: {
      type: String,
    },
    placeholder: {
      type: String,
    },
    required: {
      type: Boolean,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    active: false,
    isInvalid: false,
    screenWidth: useScreenWidth(),
  }),
  computed: {
    isHeader() {
      return (
        !!this.value || !!this.$slots?.header?.()?.find((el) => el?.text?.trim() || el?.children)
      );
      // return !!this.$slots?.header?.()?.find((el) => el?.text?.trim() || el?.children);
    },
    isDefaultSlot() {
      return !!this.$slots?.default?.()?.find((el) => el?.tag || el?.children);
    },
  },
  watch: {
    active(prevState) {
      if (prevState) {
        document.addEventListener('click', this.closeSelect);
      } else {
        document.removeEventListener('click', this.closeSelect);
      }
    },
    value(newData) {
      if (newData) this.isInvalid = false;
    },
  },
  methods: {
    closeSelect({ target }) {
      if (target.closest('.def-select-wrapper') !== this.$el) {
        this.active = false;
      }
    },
    handlerToggle() {
      if (this.isDefaultSlot) {
        this.active = !this.active;
      }
    },
    handlerClick() {
      this.active = false;
      this.isInvalid = false;
      const { hiddenInput } = this.$refs;

      if ('createEvent' in document && hiddenInput) {
        const evt = document.createEvent('HTMLEvents');
        evt.initEvent('change', true, true);
        hiddenInput.dispatchEvent(evt);
      } else hiddenInput?.fireEvent('onchange');
    },
  },
};
</script>

<style scoped lang="scss">
.def-select-wrapper {
  width: 100%;
  position: relative;
  font-size: rem(14);
  @include point-md {
    font-size: rem(16);
  }
  &_disabled {
    pointer-events: none;
    opacity: 0.7;
  }
  &__front {
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-height: 44px;
    max-height: 54px;
    position: relative;
    height: 100%;
    border: 1px solid #c8c8c8;
    transition: 0.2s linear border-color;
    padding: 8px 52px 8px 16px;
    &_active {
      border-color: #c8c8c8;
      cursor: pointer;
    }
    &_error {
      border-color: var(--background-red);
    }
    &:hover {
      color: var(--color-link-hover-dark);
    }
  }
  &__placeholder {
    transition: 0.2s linear top, 0.2s linear font-size;
    white-space: nowrap;
    overflow: hidden;
    display: block;
    text-overflow: ellipsis;
    color: #5f5f67;
    max-width: calc(100% - 18px);
    font-size: 14px;
    font-weight: 500;
    line-height: 125%;
  }
  &__center {
    font-size: rem(16);
  }
  &__drop {
    position: absolute;
    left: 0;
    top: calc(100% - 1px);
    background-color: #fff;
    z-index: 6;
    min-width: 100%;
  }
  &__scroll {
    max-height: 250px;
    border: 1px solid #cacece;
  }
  &__items {
    :deep(> *) {
      display: flex;
      align-items: center;
      padding: 8px 12px;
      cursor: pointer;
      transition: 0.2s linear background-color;
      &:hover {
        background-color: #fafafa;
      }
    }
    :deep(> .disabled) {
      pointer-events: none;
      opacity: 0.3;
    }
  }
  &__close {
    position: absolute;
    transition: 0.2s linear transform;
    right: 16px;
    width: 20px;
    height: 20px;
    fill: #5f5f67;
    top: 50%;
    transform: translateY(-50%);
    &_active {
      transform: rotate(180deg) translateY(50%);
    }
  }
  &__search {
    display: block;
    position: relative;
    padding-top: 8px;
    :deep(input) {
      width: 100%;
      height: 42px;
      border: none;
      border: 1px solid var(--background-black);
      font-weight: 500;
      font-size: rem(16);
      padding: 0 30px 0 12px;
    }
  }
  &__magnifying {
    display: block;
    width: 24px;
    height: 24px;
    position: absolute;
    top: 50%;
    right: 12px;
    transform: translateY(-50%);
  }
  &__input {
    width: 1px;
    opacity: 0;
    position: absolute;
    pointer-events: none;
  }
  &__header {
    font-size: 16px;
    font-weight: 500;
    line-height: 125%;
  }
}
</style>
